"use client";

import { useCallback, useEffect } from "react";

type Theme = "light" | "dark";

const THEME_STORAGE_KEY = "theme";
const DARK_MEDIA_QUERY = "(prefers-color-scheme: dark)";

let hasInitialized = false;

const applyThemeToDocument = (theme: Theme) => {
	if (typeof document === "undefined") {
		return;
	}

	document.documentElement.classList.toggle("dark", theme === "dark");
};

const resolveInitialTheme = (): Theme => {
	if (typeof window === "undefined") {
		return "light";
	}

	const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
	if (stored === "light" || stored === "dark") {
		return stored;
	}

	if (window.matchMedia?.(DARK_MEDIA_QUERY).matches) {
		return "dark";
	}

	return "light";
};

const persistTheme = (theme: Theme) => {
	if (typeof window === "undefined") {
		return;
	}

	window.localStorage.setItem(THEME_STORAGE_KEY, theme);
	applyThemeToDocument(theme);
};

export const useThemeToggle = () => {
	useEffect(() => {
		if (hasInitialized) {
			return;
		}

		hasInitialized = true;

		try {
			const initialTheme = resolveInitialTheme();
			applyThemeToDocument(initialTheme);
		} catch {
			// Ignore storage or media query errors
		}
	}, []);

	return useCallback(() => {
		if (typeof document === "undefined") {
			return;
		}

		const currentTheme: Theme = document.documentElement.classList.contains("dark")
			? "dark"
			: "light";
		const nextTheme: Theme = currentTheme === "dark" ? "light" : "dark";

		try {
			persistTheme(nextTheme);
		} catch {
			applyThemeToDocument(nextTheme);
		}
	}, []);
};
